/**
 * AppLayout — the authenticated MUI shell: a permanent left Drawer (the
 * sectioned Run / Set-up nav + current-user chip) and a sticky AppBar (tenant
 * identity + help), wrapping the routed content in the app-wide ToastProvider.
 *
 * Built on the U1 design system (`@/theme`): the Drawer/AppBar density and the
 * page surfaces come from the theme; named Material Symbols glyphs render via the
 * `<Icon>` ligature wrapper. The route map and `RequireAdmin` route-gating land
 * in U3 — here a gated leaf only shows the *locked affordance* when the
 * operator is not an admin.
 *
 * Nav model (DNKADM-0285): `./navModel` defines the Run (daily, always visible)
 * and Set up (collapsed-by-default groups, hidden entirely in Front Desk view)
 * sections; every leaf keeps the exact route it had under the old flat
 * Operations/Configuration arrays. Round 2 made each leaf's own `gated` flag —
 * a direct mirror of `RequireAdmin` in `src/router.tsx` — the sole source for
 * the locked affordance and Front-Desk visibility, independent of which
 * section/group presents it (Concept B deliberately mixes gated and ungated
 * leaves within a section). Group expand/collapse state persists via
 * `./navGroupExpansion`, mirroring the `ThemeModeContext` localStorage pattern.
 *
 * Collapsible rail (DNKADM-0302): a footer control collapses the drawer to a
 * ~60px icon rail (persisted via `./navCollapse`, per device; DNKADM-0346
 * widened it from 56px so its icon buttons clear the 44px touch-target
 * floor). The target
 * device is a hoverless tablet, so the rail never RELIES on tooltips: every
 * rail control carries an accessible name (`aria-label`), groups open a TAP
 * flyout `Menu` listing their children with full labels, and the gated/pending
 * affordances move into `Badge` overlays on the icons. Keyboard operation is
 * the native button/menu behavior (tab + Enter, arrow keys inside the flyout).
 * DNKADM-0303 layers a desktop-convenience tooltip on each rail icon (see
 * `railTooltip`) — hover/keyboard-focus only, touch trigger explicitly off so
 * long-press never fights the tap flyout; the aria-labels stay authoritative.
 *
 * Sign-out reuses the existing flow verbatim: `authApi.logout()` (best-effort) →
 * `useAuth().signOut()` → navigate to `/auth/signed-out`.
 *
 * Phone shell (DNKADM-0346): below the `sm` breakpoint (600px — comfortably above the
 * 390px target floor, so it never fires at tablet/desktop widths) the permanent nav
 * column becomes a temporary MUI `Drawer` overlay — closed by default, opened via a
 * hamburger `IconAction` in the `AppBar` (mobile only), and closed on navigation (the
 * existing pathname-change effect). The persisted rail-collapse toggle (`useNavCollapse`,
 * DNKADM-0302) is untouched — its storage contract, `collapsed` value, and desktop/tablet
 * rendering are unchanged; the overlay simply never renders the mini rail (an on-demand
 * drawer showing only icons defeats its own purpose), so the toggle control itself doesn't
 * render on a phone either. `showRail` (`collapsed && !isMobile`) is the one place that
 * distinction is made — it never feeds back into what gets persisted.
 *
 * STAFF shell (DNKADM-0359, the customer/staff split): a STAFF-tier operator
 * (`operator.isStaffTier`) enters the console — `AuthGate` now asks
 * `isOperator`, not `isAdmin` — and this shell shapes what it shows so that
 * operator never sees a door dink-api locks. Since DNKADM-0375 STAFF is front of
 * house: every RUN leaf, no SET UP leaf. Every hide is a HIDE, never the locked
 * affordance: a lock says "ask for access", and a STAFF member already has the
 * access they are supposed to have. Concretely — only the leaves `staffSeesLeaf`
 * accepts render (so the whole Set-up section is absent), the notifications bell is not rendered and its feed not
 * polled, Front Desk mode is not offered, the role line reads "Staff", and a
 * STAFF operator with no assigned locations gets one explicit empty state in
 * place of the routed content. Nothing here authorizes anything: `isAdmin` and
 * every consumer of it are untouched, and the server stays the ceiling.
 */
import {
  Alert,
  AlertTitle,
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link as MuiLink,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { IconAction } from '@/components/IconAction';
import { authApi } from '@/api/auth';
import { useAuth, useCurrentOperator, type CurrentOperator } from '@/auth/authHooks';
import { useFrontDeskView } from '@/auth/frontDeskHooks';
import { env } from '@/config/env';
import { HOME_PATH } from '@/routes/home';
import { LocationProvider } from '@/location/LocationContext';
import { TenantZoneProvider } from '@/location/TenantZoneProvider';
import { useServiceLocation } from '@/location/useServiceLocation';
import { Icon, initialsOf, resolveTokens } from '@/theme';
import { NotificationsProvider } from '@/notifications/NotificationsContext';
import { NotificationsBell } from '@/notifications/NotificationsBell';
import { usePendingConfirmationCount } from '@/features/confirmations/usePendingConfirmationCount';
import { useResource } from '@/hooks/useResource';
import { getMyStaff, isStaffNotLinked } from '@/api/staff';
import { WalkInSaleDialog } from '@/features/members/WalkInSaleDialog';
import { AskDinkLauncher } from '@/features/ask-dink/AskDinkLauncher';
import { ToastProvider } from './ToastProvider';
import { HelpMenu } from './HelpMenu';
import { StaleBuildNotice } from './StaleBuildNotice';
import { ExitFrontDeskDialog } from './ExitFrontDeskDialog';
import { LocationChooser } from './LocationChooser';
import { LocationTabTitle } from './LocationTabTitle';
import {
  ALL_NAV_PATHS,
  LOCATION_SCOPED_NAV_PATHS,
  RUN_SECTION,
  SETUP_SECTION,
  activeGroupId,
  activeNavPath,
  staffSeesLeaf,
  type NavGroup,
  type NavLeaf,
} from './navModel';
import { useGroupExpansion } from './navGroupExpansion';
import { useNavCollapse } from './navCollapse';
import dinkMark from '@/assets/dink-mark.png';

const DRAWER_WIDTH = 248;
// 60px, not 56 (DNKADM-0346): at 56px the rail's own horizontal padding left
// each icon button measuring 43px wide in a real browser — 1px under the
// 44px shell touch-target floor. 60px gives comfortable headroom.
/** Collapsed mini icon rail (DNKADM-0302) — icons only, no labels. */
const RAIL_WIDTH = 60;
const APP_BAR_HEIGHT = 64;

/** Human-readable role label for the user chip (Owner/Admin vs. the raw role). */
function roleLabel(operator: CurrentOperator): string {
  // A platform-support session names itself, ahead of the tenant roles
  // (DNKADM-0356): its token holds SUPER_ADMIN, but the operator is a platform
  // principal visiting this tenant, not one of its admins. The name/avatar beside
  // this line stay the operator's real identity — saas-auth's frozen token
  // contract puts the platform operator's own `name`/`email` there, never a
  // tenant user's.
  if (operator.isPlatformSupport) return 'Platform support';
  if (operator.roles.includes('OWNER')) return 'Owner';
  if (operator.roles.includes('SUPER_ADMIN')) return 'Super Admin';
  if (operator.roles.includes('ADMIN')) return 'Admin';
  // The staff floor (DNKADM-0359) — named, not left to the raw-role fallback
  // below, which would read "STAFF".
  if (operator.isStaffTier) return 'Staff';
  return operator.roles[0] ?? 'Member';
}

/**
 * True when this session is a STAFF operator whose location list loaded
 * successfully and came back EMPTY — no OWNER has assigned them locations yet
 * (DNKADM-0359; dink-api HANDOFF §2 / DNKAPI-0519). That is a STATE, not a bug
 * and not an `error`: `LocationStatus` keeps its three fail-closed values and
 * this reads `ready` + zero rows beside the operator's tier, so no consumer of
 * the location context changes.
 *
 * For OWNER/ADMIN an empty list keeps today's behaviour — it means the TENANT
 * has no locations, which is a different situation with a different fix.
 */
function useNoAssignedLocations(isStaffTier: boolean): boolean {
  const { status, locations } = useServiceLocation();
  return isStaffTier && status === 'ready' && locations.length === 0;
}

/**
 * The app-bar location chooser, withheld from a STAFF session with no assigned
 * locations (DNKADM-0359) — there is nothing to choose between, and an empty
 * chooser reads as a broken control. A thin wrapper rather than a branch inside
 * `LocationChooser` because it is the operator's TIER that decides, and the
 * chooser is deliberately auth-free (it renders in trees with no AuthProvider).
 */
function LocationChooserSlot({ isStaffTier }: { isStaffTier: boolean }) {
  const noAssignedLocations = useNoAssignedLocations(isStaffTier);
  return noAssignedLocations ? null : <LocationChooser />;
}

/**
 * The routed content, replaced by the assignment empty state for a STAFF
 * session with no locations — on the location-scoped destinations ONLY
 * (DNKADM-0359). Mounted INSIDE `LocationProvider`, so it can read the load
 * state the shell itself (which creates the provider) cannot, and INSIDE the
 * main content region, so the nav is never lost.
 *
 * Which destinations those are comes from the NAV MODEL
 * (`LOCATION_SCOPED_NAV_PATHS`, derived from each leaf's `locationScoped` flag
 * and covered by the drift guard), resolved with the same longest-prefix helper
 * the active-leaf highlight uses — so `/reservations/pending` is covered and no
 * hand-kept URL list can drift. It is hide-by-nav-leaf, not URL matching
 * against a gate table.
 *
 * Why it is scoped rather than blanket: Policies reads a PUBLIC tenant-wide
 * bundle and needs no location at all, so an operator waiting on an assignment
 * can still read it. Replacing every route would leave that operator a nav
 * whose leaves answer with an unrelated message — the shell advertising a door
 * and then refusing it, which is the failure this whole unit removes.
 */
function LocationScopedOutlet({ isStaffTier }: { isStaffTier: boolean }) {
  const { pathname } = useLocation();
  const noAssignedLocations = useNoAssignedLocations(isStaffTier);
  const locationScoped = activeNavPath(pathname, LOCATION_SCOPED_NAV_PATHS) !== null;
  if (!noAssignedLocations || !locationScoped) return <Outlet />;
  return <NoAssignedLocationsState />;
}

/**
 * The assignment empty state itself, extracted (DNKADM-0363) so its resolver
 * mounts ONLY when the state actually renders. It resolves `GET /v2/me/staff`
 * to answer one question — is this login linked to a coach record? — and, when
 * it is, offers My schedule: an operator waiting on a location assignment can
 * still read their own hours, exceptions and bookings, none of which needs the
 * app-level location selection.
 *
 * The link is CONDITIONAL for the same reason the prose pointer to Team &
 * access is not a link at all: a link that lands on "your login isn't linked
 * yet" is another door that leads nowhere. An unlinked login, a support
 * session, or a failed resolve all render the empty state exactly as
 * DNKADM-0359 shipped it — the offer is additive and fail-soft.
 *
 * Resolved on mount, in memory, never stored — the same rule the screen
 * itself follows.
 */
function NoAssignedLocationsState() {
  const myStaff = useResource(useCallback(() => getMyStaff(), []), []);
  const linked =
    myStaff.status === 'ready' && myStaff.data !== null && !isStaffNotLinked(myStaff.data);
  return (
    <Box sx={{ mt: 2.5, maxWidth: 620 }}>
      <Alert severity="info" icon={<Icon name="apartment" sx={{ fontSize: 22 }} />}>
        <AlertTitle sx={{ fontWeight: 500 }}>No locations are assigned to you yet</AlertTitle>
        Ask an owner to assign your locations in Team &amp; access.
        {linked && (
          <Box sx={{ mt: 1 }}>
            <MuiLink component={RouterLink} to="/my-schedule">
              Meanwhile, see My schedule
            </MuiLink>
          </Box>
        )}
      </Alert>
    </Box>
  );
}

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  // Chrome colors read the semantic token namespace (DNKADM-0266) so the
  // shell flips with the color mode; resolveTokens is the test-safe read.
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const mainRef = useRef<HTMLElement>(null);
  const previousPathname = useRef(location.pathname);
  const activePath = activeNavPath(location.pathname, ALL_NAV_PATHS);
  // The group (if any) containing the active leaf — it always renders open
  // (DNKADM-0285 auto-expand); see useGroupExpansion.
  const currentActiveGroupId = activeGroupId(activePath);
  const { isExpanded, toggle } = useGroupExpansion();
  // Collapsed mini-rail state (DNKADM-0302) — persisted per device.
  const { collapsed, toggleCollapsed } = useNavCollapse();
  // The one open rail flyout (collapsed mode): which group, anchored where.
  const [flyout, setFlyout] = useState<{ anchor: HTMLElement; groupId: string } | null>(null);
  // Phone shell (DNKADM-0346) — below `sm` the drawer becomes an on-demand overlay.
  // `mobileNavOpen` is deliberately ephemeral (no persistence): it always starts
  // closed and closes again on navigation, unlike `collapsed` above.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // The mini rail is a permanent-column density trick; it never applies inside
  // the on-demand mobile overlay, which always shows the full nav.
  const showRail = collapsed && !isMobile;
  const { signOut } = useAuth();
  const operator = useCurrentOperator();
  const { frontDeskView, enterFrontDeskView } = useFrontDeskView();
  const [signingOut, setSigningOut] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmFrontDesk, setConfirmFrontDesk] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  // The Front Desk primary action (DNKADM-0308): the one capability that GAINS in Front
  // Desk view rather than being hidden — never `gated`, present whenever `frontDeskView`.
  const [walkInOpen, setWalkInOpen] = useState(false);
  // The tenant-wide pending count behind the Reservations badge (DNKADM-0287 —
  // moved from the retired Confirmations leaf) — the shared event-synced source
  // the Pending tab and Now & Next's header also read (DNKADM-0276).
  const confirmationCount = usePendingConfirmationCount() ?? 0;

  const isAdmin = operator?.isAdmin ?? false;
  // The staff FLOOR (DNKADM-0359) — *may enter, may not configure*. It shapes
  // what the SHELL renders (which nav leaves, the bell, Front Desk, the
  // empty-location state); it never opens anything, and `isAdmin` above stays
  // the one answer to "may you configure".
  const isStaffTier = operator?.isStaffTier ?? false;
  // Platform-support session (DNKADM-0356). DISPLAY + Front-Desk availability
  // only — it never gates a surface: a support token holds SUPER_ADMIN and passes
  // exactly the gates a tenant SUPER_ADMIN passes, so `isAdmin` above (and every
  // consumer of it) is deliberately untouched.
  const isPlatformSupport = operator?.isPlatformSupport ?? false;
  // Front Desk mode is withheld from a platform-support session (DNKADM-0356 —
  // its password-gated exit can never succeed), from a STAFF session
  // (DNKADM-0359 — STAFF is already the restricted view, so there is nothing to
  // hand off) and, until saas-auth's SASATH-0321 lands, from every TENANT_STAFF
  // session including OWNER/ADMIN (DNKADM-0368 — post-cutover their exit re-auth
  // resolves through a membership row that moved to `tenant_staff`). Read from
  // the SHARED derivation in `authContextValue` — the same flag
  // `FrontDeskContext` forces the mode off with, so the two cannot drift.
  const frontDeskUnavailable = operator?.frontDeskUnavailable ?? false;

  /**
   * The persistent support-session indicator (DNKADM-0356) — a state badge, not a
   * control: no `onClick`, and nothing keys off it but its own visibility. Colors
   * come from the existing warn-soft token trio, so it reads in both color modes
   * and adds no pair to the contrast audit.
   */
  const platformSupportChip = (
    <Chip
      icon={<Icon name="support_agent" sx={{ fontSize: 18 }} />}
      label="Platform support session"
      size="small"
      sx={{
        bgcolor: tokens.warnSoft,
        color: tokens.warnSoftInk,
        border: `1px solid ${tokens.warnSoftLine}`,
        fontWeight: 500,
        '& .MuiChip-icon': { color: 'inherit' },
      }}
    />
  );

  useEffect(() => {
    if (previousPathname.current === location.pathname) return;
    previousPathname.current = location.pathname;
    mainRef.current?.focus({ preventScroll: true });
    // Phone shell (DNKADM-0346): the on-demand overlay closes on navigation —
    // a no-op when it's already closed (desktop/tablet, or a mobile session
    // that didn't open it).
    setMobileNavOpen(false);
  }, [location.pathname]);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await authApi.logout();
    } catch {
      // A failed network logout must not block clearing the local session.
    }
    signOut();
    navigate('/auth/signed-out', { replace: true });
  }, [navigate, signOut]);

  const handleConfirmFrontDesk = useCallback(() => {
    setConfirmFrontDesk(false);
    enterFrontDeskView();
    // Land on the daily board (home) — Set up is now out of reach until the
    // gated exit.
    navigate(HOME_PATH);
  }, [enterFrontDeskView, navigate]);

  /**
   * Whether this session sees a leaf AT ALL. Two independent hides, both
   * "not rendered" rather than "rendered locked":
   *  - **Front Desk view** hides every `gated` leaf (DNKADM-0285 round 2);
   *  - a **STAFF session** hides every leaf {@link staffSeesLeaf} rejects — the
   *    SET UP leaves (DNKADM-0375). The lock affordance is the wrong answer for
   *    them: a lock says "ask for access", and a STAFF member already has the
   *    access they are supposed to have.
   *  - a **platform-support session** hides every `selfScoped` leaf
   *    (DNKADM-0363). Those destinations' subject is the operator's OWN staff
   *    record, and a support session has no roster row by construction
   *    (DNKADM-0356) — dink-api answers its `GET /v2/me/staff` with `403
   *    NOT_TENANT_STAFF`. Hiding the leaf is not the gate: the route stays
   *    reachable and the screen renders that `403` honestly if it is typed.
   *
   * The first two hides never touch OWNER/ADMIN/SUPER_ADMIN; the third touches
   * only a support session, for whom `isAdmin` and every gate remain untouched.
   */
  const leafVisible = (leaf: NavLeaf) =>
    !(frontDeskView && leaf.gated) &&
    !(isStaffTier && !staffSeesLeaf(leaf)) &&
    !(isPlatformSupport && leaf.selfScoped);

  /**
   * The Set-up entries this session sees — derived from `leafVisible`, not from
   * "STAFF hides Set up", so a future ungated Set-up leaf would still
   * render rather than being silently swallowed by a hard-coded section rule.
   * Today every Set-up leaf is admin configuration, so a STAFF session gets an
   * empty list and the section (its label and lock included) is absent.
   */
  const setupEntries = SETUP_SECTION.entries.filter((entry) =>
    entry.kind === 'item' ? leafVisible(entry) : entry.children.some(leafVisible),
  );

  /**
   * A leaf item. The locked affordance follows the leaf's own `gated` flag —
   * a direct mirror of `RequireAdmin` in `src/router.tsx` — not the section
   * it's presented in (DNKADM-0285 round 2).
   */
  const renderNavItem = (item: NavLeaf) => {
    // An item is active when it is the most specific nav path matching the
    // current route; see activeNavPath.
    const active = item.path === activePath;
    const locked = item.gated && !isAdmin;
    return (
      <ListItemButton
        key={item.path}
        component={RouterLink}
        to={item.path}
        selected={active}
        aria-current={active ? 'page' : undefined}
        sx={{
          borderRadius: 2,
          // 44px — shell touch-target floor (DNKADM-0346).
          minHeight: 44,
          mb: '2px',
          color: 'text.secondary',
          '& .MuiListItemIcon-root': { color: tokens.text2 },
          '&:hover': { bgcolor: tokens.brandFaint },
          '&.Mui-selected': {
            bgcolor: tokens.brandSoft,
            color: tokens.brand,
            '& .MuiListItemIcon-root': { color: tokens.brand },
            '&:hover': { bgcolor: tokens.brandSoft2 },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Icon name={item.icon} sx={{ fontSize: 21 }} />
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          slotProps={{
            primary: { sx: { fontSize: 14, fontWeight: 500 } },
          }}
        />
        {locked ? (
          <Icon
            name="lock"
            title="Owner/Admin only"
            sx={{ fontSize: 16, color: tokens.textGhost }}
          />
        ) : item.path === '/reservations' && confirmationCount > 0 ? (
          <Chip
            label={confirmationCount}
            size="small"
            sx={{
              height: 22,
              minWidth: 22,
              bgcolor: tokens.warnBadgeBg,
              color: tokens.warnBadgeInk,
              fontWeight: 700,
            }}
          />
        ) : null}
      </ListItemButton>
    );
  };

  /**
   * A collapsible group header + its children (DNKADM-0285). The header is a
   * keyboard-operable disclosure control (`aria-expanded`, native `<button>`
   * via the default `ListItemButton` component) that toggles persisted
   * expand/collapse state; the group containing the active route always
   * renders open (`useGroupExpansion`'s auto-expand). `children` defaults to
   * every child in the group; the Run section passes a Front-Desk-filtered
   * subset (see the render below) so a gated child can be dropped without
   * touching the group's own definition.
   */
  const renderGroup = (group: NavGroup, children: NavLeaf[] = group.children) => {
    const isActiveGroup = currentActiveGroupId === group.id;
    const open = isExpanded(group, isActiveGroup);
    const panelId = `nav-group-panel-${group.id}`;
    return (
      <Box key={group.id}>
        <ListItemButton
          onClick={() => toggle(group, isActiveGroup)}
          aria-expanded={open}
          aria-controls={panelId}
          sx={{
            borderRadius: 2,
            // 44px — shell touch-target floor (DNKADM-0346).
            minHeight: 44,
            mb: '2px',
            color: isActiveGroup ? tokens.brand : 'text.secondary',
            '& .MuiListItemIcon-root': { color: isActiveGroup ? tokens.brand : tokens.text2 },
            '&:hover': { bgcolor: tokens.brandFaint },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Icon name={group.icon} sx={{ fontSize: 21 }} />
          </ListItemIcon>
          <ListItemText
            primary={group.label}
            slotProps={{
              primary: { sx: { fontSize: 14, fontWeight: 500 } },
            }}
          />
          <Icon
            name={open ? 'expand_less' : 'expand_more'}
            sx={{ fontSize: 20, color: tokens.text2 }}
          />
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit={false}>
          <List id={panelId} disablePadding aria-hidden={!open} sx={{ pl: 1.5 }}>
            {children.map((child) => renderNavItem(child))}
          </List>
        </Collapse>
      </Box>
    );
  };

  /**
   * Collapsed-rail tooltip (DNKADM-0303): names an icon-only control on hover
   * AND keyboard focus, collapsed state only (the expanded drawer shows the
   * label itself). `disableTouchListener` is the explicit touch choice: the
   * default long-press-to-open would fight the rail's tap interactions (a
   * group tap opens the flyout), and the hoverless target device identifies
   * icons by the DNKADM-0302 audit + aria-labels, not by tooltip.
   * `disableInteractive` keeps the tooltip from lingering over the pointer's
   * path to the next rail item. Additive: the child keeps its own aria-label.
   */
  const railTooltip = (title: string, child: ReactElement, key?: string) => (
    <Tooltip key={key} title={title} placement="right" disableTouchListener disableInteractive>
      {child}
    </Tooltip>
  );

  /**
   * A leaf on the collapsed icon rail (DNKADM-0302): icon-only, so the
   * accessible name moves to `aria-label` (folding in the pending count and
   * the locked affordance the expanded row shows as trailing content), and
   * those affordances render as `Badge` overlays. The rail never RELIES on
   * tooltips (hoverless target device); DNKADM-0303 adds one per icon as a
   * desktop convenience (`railTooltip`).
   */
  const renderRailItem = (item: NavLeaf) => {
    const active = item.path === activePath;
    const locked = item.gated && !isAdmin;
    const pendingCount = item.path === '/reservations' ? confirmationCount : 0;
    const railName = locked
      ? `${item.label} (Owner/Admin only)`
      : pendingCount > 0
        ? `${item.label} (${pendingCount} pending)`
        : item.label;
    return railTooltip(
      railName,
      <ListItemButton
        component={RouterLink}
        to={item.path}
        selected={active}
        aria-current={active ? 'page' : undefined}
        aria-label={railName}
        sx={{
          borderRadius: 2,
          minHeight: 44,
          mb: '2px',
          justifyContent: 'center',
          px: 0,
          color: tokens.text2,
          '&:hover': { bgcolor: tokens.brandFaint },
          '&.Mui-selected': {
            bgcolor: tokens.brandSoft,
            color: tokens.brand,
            '&:hover': { bgcolor: tokens.brandSoft2 },
          },
        }}
      >
        <Badge
          badgeContent={
            locked ? (
              <Icon name="lock" sx={{ fontSize: 11 }} />
            ) : pendingCount > 0 ? (
              pendingCount
            ) : null
          }
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': locked
              ? { bgcolor: 'transparent', color: tokens.textGhost, right: -2, top: 2, p: 0 }
              : {
                  bgcolor: tokens.warnBadgeBg,
                  color: tokens.warnBadgeInk,
                  fontWeight: 700,
                  right: -2,
                  top: 2,
                },
          }}
        >
          <Icon name={item.icon} sx={{ fontSize: 22 }} />
        </Badge>
      </ListItemButton>,
      item.path,
    );
  };

  /**
   * A group on the collapsed rail (DNKADM-0302): a tap (not hover) opens a
   * flyout `Menu` of the group's children with their full labels — the rail's
   * second interaction pattern. The Menu is keyboard-operable out of the box
   * (arrow keys, Enter, Escape) and each child keeps its locked affordance.
   * The trigger's tooltip (DNKADM-0303) names the GROUP on hover/focus only —
   * the tap keeps opening the flyout, untouched.
   */
  const renderRailGroup = (group: NavGroup, children: NavLeaf[] = group.children) => {
    const isActiveGroup = currentActiveGroupId === group.id;
    const open = flyout?.groupId === group.id;
    return (
      <Box key={group.id}>
        {railTooltip(
          group.label,
          <ListItemButton
            onClick={(event) =>
              setFlyout(open ? null : { anchor: event.currentTarget, groupId: group.id })
            }
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={group.label}
            selected={isActiveGroup}
            sx={{
              borderRadius: 2,
              minHeight: 44,
              mb: '2px',
              justifyContent: 'center',
              px: 0,
              color: tokens.text2,
              '&:hover': { bgcolor: tokens.brandFaint },
              '&.Mui-selected': {
                bgcolor: tokens.brandSoft,
                color: tokens.brand,
                '&:hover': { bgcolor: tokens.brandSoft2 },
              },
            }}
          >
            <Icon name={group.icon} sx={{ fontSize: 22 }} />
          </ListItemButton>,
        )}
        <Menu
          anchorEl={open ? (flyout?.anchor ?? null) : null}
          open={open}
          onClose={() => setFlyout(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {children.map((child) => {
            const active = child.path === activePath;
            const childLocked = child.gated && !isAdmin;
            return (
              <MenuItem
                key={child.path}
                component={RouterLink}
                to={child.path}
                selected={active}
                aria-current={active ? 'page' : undefined}
                onClick={() => setFlyout(null)}
                sx={{ minHeight: 44 }}
              >
                <ListItemIcon sx={{ color: active ? tokens.brand : tokens.text2 }}>
                  <Icon name={child.icon} sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={child.label}
                  slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
                />
                {childLocked ? (
                  <Icon
                    name="lock"
                    title="Owner/Admin only"
                    sx={{ fontSize: 16, color: tokens.textGhost, ml: 1.5 }}
                  />
                ) : null}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    );
  };

  // The nav panel's content (DNKADM-0346) — shared verbatim between the
  // permanent desktop/tablet column and the mobile temporary-overlay Drawer
  // (see the two-branch wrapper in the return below). `showRail` (not
  // `collapsed`) governs every rendering choice here — see the DNKADM-0346
  // doc paragraph above the component.
  const navPanelContent = (
    <>
      {/* Logo lockup — collapsed keeps only the mark, centered. */}
      <Box
        sx={{
          height: APP_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: showRail ? 'center' : 'flex-start',
          gap: 1.25,
          px: showRail ? 0 : 2.25,
          borderBottom: `1px solid ${tokens.lineSoft}`,
        }}
      >
        {/* The Dink mark (DNKADM-0337 U4) — a self-contained circular mark on its own
                transparency, no backing plate: its green is close to COURT_GREEN, so a plate
                would swallow it. Bundled from src/assets (hashed), unlike the OS-fetched icons. */}
        <Box
          component="img"
          src={dinkMark}
          alt="Dink Admin"
          sx={{ width: 34, height: 34, flexShrink: 0 }}
        />
        {!showRail ? (
          <>
            <Typography sx={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.5px' }}>
              Admin
            </Typography>
            {env.deployEnv === 'staging' ? (
              <Chip
                label="Staging"
                aria-label="Staging environment"
                size="small"
                sx={{
                  height: 22,
                  bgcolor: 'warning.light',
                  color: 'warning.contrastText',
                  fontSize: 11,
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ) : null}
          </>
        ) : null}
      </Box>

      <Box sx={{ px: showRail ? 0.75 : 1.25, py: 1, flex: 1, overflowY: 'auto' }}>
        {!showRail ? <SectionLabel>{RUN_SECTION.label}</SectionLabel> : null}
        <List disablePadding>
          {RUN_SECTION.entries.map((entry) => {
            // Run is always rendered as a section, but its leaves are filtered
            // per session: a few are still RequireAdmin-gated (Config changes),
            // which Front Desk view hides individually, same as it would if
            // they sat in Set up (DNKADM-0285 round 2); and a STAFF session
            // hides every leaf dink-api closes to it (DNKADM-0359). A group
            // left with no visible children after filtering doesn't render.
            if (entry.kind === 'item') {
              if (!leafVisible(entry)) return null;
              return showRail ? renderRailItem(entry) : renderNavItem(entry);
            }
            const visibleChildren = entry.children.filter(leafVisible);
            if (visibleChildren.length === 0) return null;
            return showRail
              ? renderRailGroup(entry, visibleChildren)
              : renderGroup(entry, visibleChildren);
          })}
        </List>

        {/* The ENTIRE Set-up section is hidden in Front Desk view — only
                Run is reachable until the password-gated exit — and equally
                for a STAFF session, which has no visible Set-up leaf at all
                (DNKADM-0359: hidden, not a section full of locks). */}
        {!frontDeskView && setupEntries.length > 0 ? (
          <>
            {showRail ? (
              <Divider sx={{ my: 1, borderColor: tokens.lineSoft }} />
            ) : (
              <SectionLabel>
                {SETUP_SECTION.label}
                {!isAdmin ? (
                  <Icon
                    name="lock"
                    title="Owner/Admin only"
                    sx={{ fontSize: 14, color: tokens.textGhost, ml: 0.5, verticalAlign: 'middle' }}
                  />
                ) : null}
              </SectionLabel>
            )}
            <List disablePadding>
              {setupEntries.map((entry) =>
                entry.kind === 'item'
                  ? showRail
                    ? renderRailItem(entry)
                    : renderNavItem(entry)
                  : showRail
                    ? renderRailGroup(entry, entry.children.filter(leafVisible))
                    : renderGroup(entry, entry.children.filter(leafVisible)),
              )}
            </List>
          </>
        ) : null}
      </Box>

      {/* Collapse / expand control (DNKADM-0302) — a persistent footer
              toggle, not a hamburger: the rail keeps the operator oriented.
              Tooltip-named only while collapsed (DNKADM-0303) — expanded, the
              row shows its own label. Doesn't render inside the mobile overlay
              (DNKADM-0346) — the rail concept doesn't apply there, see above. */}
      {!isMobile ? (
        <Box sx={{ px: collapsed ? 0.75 : 1.25, py: 0.5 }}>
          {(() => {
            const toggleButton = (
              <ListItemButton
                onClick={() => {
                  setFlyout(null);
                  toggleCollapsed();
                }}
                aria-label={collapsed ? 'Expand menu' : undefined}
                sx={{
                  borderRadius: 2,
                  minHeight: 44,
                  justifyContent: 'center',
                  px: 0,
                  color: tokens.text2,
                  '&:hover': { bgcolor: tokens.brandFaint },
                }}
              >
                <Icon name={collapsed ? 'chevron_right' : 'chevron_left'} sx={{ fontSize: 21 }} />
                {!collapsed ? (
                  <ListItemText
                    primary="Collapse menu"
                    sx={{ flex: '0 1 auto', ml: 1 }}
                    slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
                  />
                ) : null}
              </ListItemButton>
            );
            return collapsed ? railTooltip('Expand menu', toggleButton) : toggleButton;
          })()}
        </Box>
      ) : null}

      {/* Current-user chip — collapsed keeps just the avatar, which still
              opens the account menu (same anchor state, same menu). */}
      {operator ? (
        <Box sx={{ p: showRail ? 1 : 1.75, borderTop: `1px solid ${tokens.lineSoft}` }}>
          {/* The support badge sits beside the operator chip inside the MOBILE
              overlay only (DNKADM-0356). At tablet/desktop the app-bar copy is
              already on screen next to this permanent column, so a second one
              here would just be noise; on a phone the bar is cramped and the
              overlay is where the operator's identity lives. The badge is never
              the ONLY indication either way — this chip's own role line reads
              "Platform support". */}
          {isPlatformSupport && isMobile ? (
            <Box sx={{ pb: 1.25 }}>{platformSupportChip}</Box>
          ) : null}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: showRail ? 'center' : 'flex-start',
              gap: 1.25,
              p: showRail ? 0 : 1,
              borderRadius: 2,
              bgcolor: showRail ? 'transparent' : tokens.raise,
            }}
          >
            {showRail ? (
              railTooltip(
                'Account menu',
                // The span keeps the Tooltip valid while the button is
                // disabled during sign-out (MUI's disabled-child rule).
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <IconButton
                    aria-label="Account menu"
                    aria-haspopup="menu"
                    onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                    disabled={signingOut}
                    // 44px — shell touch-target floor (DNKADM-0346). The 34px
                    // avatar stays visually unchanged, centered inside.
                    sx={{ width: 44, height: 44 }}
                  >
                    <Avatar sx={{ bgcolor: tokens.brand, width: 34, height: 34, fontSize: 13 }}>
                      {initialsOf(operator.name ?? operator.email)}
                    </Avatar>
                  </IconButton>
                </Box>,
              )
            ) : (
              <>
                <Avatar sx={{ bgcolor: tokens.brand, width: 34, height: 34, fontSize: 13 }}>
                  {initialsOf(operator.name ?? operator.email)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 500 }}>
                    {operator.name ?? operator.email}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 11, color: tokens.textSubtle }}>
                    {roleLabel(operator)}
                  </Typography>
                </Box>
                <Tooltip title="Account">
                  <IconButton
                    aria-label="Account menu"
                    aria-haspopup="menu"
                    onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                    disabled={signingOut}
                    // 44px — shell touch-target floor (DNKADM-0346).
                    sx={{ width: 44, height: 44 }}
                  >
                    <Icon name="more_vert" sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={() => setUserMenuAnchor(null)}
            >
              {/* Front Desk mode is unavailable to a platform-support session
                  (DNKADM-0356): its password-gated exit can never succeed for a
                  principal with no tenant membership row, so the ENTRY control is
                  not rendered at all — not rendered disabled with a tooltip. A
                  control that can never work should not exist. The exit item is
                  unreachable for that principal in consequence, and untouched.
                  A STAFF session is withheld the same way (DNKADM-0359): it is
                  already the restricted view, so there is nothing to hand off.
                  And every TENANT_STAFF session — OWNER and ADMIN included — is
                  withheld it temporarily (DNKADM-0368): after the customer/staff
                  cutover their exit re-auth resolves through a row that moved to
                  `tenant_staff`, which is the same trap. */}
              {frontDeskView ? (
                <MenuItem
                  onClick={() => {
                    setUserMenuAnchor(null);
                    setExitOpen(true);
                  }}
                >
                  <ListItemIcon>
                    <Icon name="lock_open" sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  Exit Front Desk view
                </MenuItem>
              ) : frontDeskUnavailable ? null : (
                <MenuItem
                  onClick={() => {
                    setUserMenuAnchor(null);
                    setConfirmFrontDesk(true);
                  }}
                >
                  <ListItemIcon>
                    <Icon name="point_of_sale" sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  Switch to Front Desk view
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null);
                  void handleSignOut();
                }}
                disabled={signingOut}
              >
                <ListItemIcon>
                  <Icon name="logout" sx={{ fontSize: 20 }} />
                </ListItemIcon>
                Sign out
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      ) : null}
    </>
  );

  return (
    <ToastProvider>
      {/* The service-location context (DNKADM-0033) lives inside the authenticated
          shell — `operator`/JWT exists here — so the app-bar chooser and the
          Operations screens beneath the Outlet can read it. It does not wrap
          Configuration's own per-screen selector (the Operations-only scope rule is
          enforced by who calls `useServiceLocation`, see DNKADM-0033 U2). */}
      <LocationProvider>
        {/* The tenant's default time zone (DNKADM-0478), read once for the shell: every
          screen's calendar zone falls back location -> tenant -> device through it. */}
        <TenantZoneProvider>
          {/* Browser tab title derived from the selected location (DNKADM-0230) —
            headless, so it can read the context this shell creates. */}
          <LocationTabTitle />
          {/* Stale-build notice (DNKADM-0372): a long-lived tab never re-requests the
            page, so the shell periodically compares its compiled-in build id with the
            one the server publishes and offers a Reload — never reloads by itself. */}
          <StaleBuildNotice />
          {/* System notifications (DNKADM-0047): a single shared polling context wraps the
            shell so the app-bar bell badge (and U2's popover) read one fresh list. It
            mounts inside the authenticated shell, where the JWT exists. Polling is OFF
            for a STAFF session (DNKADM-0359) — the feed is `403` for it and the bell is
            not rendered, so a 60s poll of a guaranteed `403` is pure noise. The context
            is still mounted, so any consumer reads a valid (quiet) value rather than
            throwing. */}
          <NotificationsProvider enabled={!isStaffTier}>
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
              {/* Phone shell (DNKADM-0346): below `sm`, the nav becomes a temporary
              overlay Drawer (closed by default, opened via the AppBar hamburger,
              closed on navigate) instead of a permanent flex column — it no
              longer occupies content width. Tablet/desktop keep the exact
              permanent sticky column, `collapsed` width included. */}
              {isMobile ? (
                <Drawer
                  variant="temporary"
                  open={mobileNavOpen}
                  onClose={() => setMobileNavOpen(false)}
                  slotProps={{
                    paper: {
                      component: 'nav',
                      // Overrides the Drawer's own `role="dialog"` default (its
                      // WAI-ARIA modal-dialog pattern for `variant="temporary"`):
                      // this panel IS the same Primary nav landmark the permanent
                      // column exposes, and every consumer (tests, assistive
                      // tech) should be able to find it the same way regardless
                      // of breakpoint.
                      role: 'navigation',
                      'aria-label': 'Primary',
                      sx: {
                        width: DRAWER_WIDTH,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: 'background.paper',
                      },
                    },
                  }}
                >
                  {navPanelContent}
                </Drawer>
              ) : (
                <Box
                  component="nav"
                  aria-label="Primary"
                  sx={{
                    width: collapsed ? RAIL_WIDTH : DRAWER_WIDTH,
                    transition: theme.transitions.create('width'),
                    flexShrink: 0,
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.paper',
                    borderRight: `1px solid ${tokens.line}`,
                  }}
                >
                  {navPanelContent}
                </Box>
              )}

              {/* Main column */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <AppBar position="sticky" sx={{ height: APP_BAR_HEIGHT }}>
                  <Toolbar sx={{ gap: 2, minHeight: APP_BAR_HEIGHT, height: APP_BAR_HEIGHT }}>
                    {/* Opens the mobile overlay nav (DNKADM-0346) — the shell's only
                    entry point to the drawer below `sm`, where it isn't a
                    permanent column. Absent at tablet/desktop, same as before. */}
                    {isMobile ? (
                      <IconAction
                        label="Open navigation"
                        onClick={() => setMobileNavOpen(true)}
                        // 44px — shell touch-target floor (DNKADM-0346). `ml`
                        // compensates the added box so the icon still lines up
                        // with the AppBar's left edge like the other icons.
                        sx={{ width: 44, height: 44, ml: -1.25 }}
                      >
                        <Icon name="menu" sx={{ fontSize: 22 }} />
                      </IconAction>
                    ) : null}
                    {/* The tenant chip is replaced by the service-location chooser
                    (DNKADM-0033). Tenant identity stays implicit (resolved server-side
                    from the JWT). Withheld from a STAFF session with no assigned
                    locations (DNKADM-0359) — see `LocationChooserSlot`. */}
                    <LocationChooserSlot isStaffTier={isStaffTier} />
                    <Box sx={{ flex: 1 }} />
                    {frontDeskView ? (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Icon name="point_of_sale" sx={{ fontSize: 18 }} />}
                        onClick={() => setWalkInOpen(true)}
                      >
                        Sell to a walk-in
                      </Button>
                    ) : null}
                    {frontDeskView ? (
                      <Chip
                        icon={<Icon name="point_of_sale" sx={{ fontSize: 18 }} />}
                        label="Front desk view"
                        aria-label="Front desk view"
                        onClick={() => setExitOpen(true)}
                        sx={{ bgcolor: tokens.brandSoft2, color: tokens.brand, fontWeight: 500 }}
                      />
                    ) : null}
                    {/* Platform-support session badge (DNKADM-0356) — always
                    visible, on every route and at every width, so anyone glancing
                    at the desk can tell a support session from an owner's. Compact
                    by design: the deploy-environment banner already owns the
                    full-width slot. */}
                    {isPlatformSupport ? platformSupportChip : null}
                    {/* The color-mode control lives in Settings → General only
                    (DNKADM-0304 U10) — the unlabelled app-bar icon was a second
                    entry point to the same stored preference (its menu DID offer
                    all three modes, so nothing is lost), and the bar's width
                    matters most on tablet. */}
                    {/* Ask Dink (DNKADM-0434) — button, `?` shortcut and the
                    right-side panel; hides itself when the assistant is off. */}
                    <AskDinkLauncher />
                    {/* System notifications bell — renders in BOTH modes (the Toolbar
                    always renders; DNKADM-0047). Not rendered for a STAFF session
                    (DNKADM-0359): `/v2/admin/notifications` is `403` for it
                    (HANDOFF §2), so the bell could only ever open an empty panel.
                    Hidden, not disabled — and the feed is not polled either (the
                    `NotificationsProvider` wrapping this shell is mounted
                    disabled). */}
                    {isStaffTier ? null : <NotificationsBell />}
                    <HelpMenu />
                  </Toolbar>
                </AppBar>

                <Box
                  ref={mainRef}
                  component="main"
                  tabIndex={-1}
                  sx={{ flex: 1, overflowY: 'auto', px: 4, pt: 3.5, pb: 10 }}
                >
                  <Box
                    sx={{
                      maxWidth: '90%',
                      mx: 'auto',
                      '@media (min-width: 1921px)': {
                        maxWidth: '80%',
                      },
                    }}
                  >
                    {/* The routed content, or the assignment empty state for a
                    STAFF session with no locations (DNKADM-0359). */}
                    <LocationScopedOutlet isStaffTier={isStaffTier} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Confirm entering Front Desk view — no re-auth to enter. */}
            <Dialog
              open={confirmFrontDesk}
              onClose={() => setConfirmFrontDesk(false)}
              aria-labelledby="front-desk-confirm-title"
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle id="front-desk-confirm-title">Switch to Front Desk view?</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ fontSize: 14 }}>
                  Front Desk view hides Set up and shows only Run. You&rsquo;ll need your password to
                  switch back.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmFrontDesk(false)} color="inherit">
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleConfirmFrontDesk}>
                  Switch to Front Desk view
                </Button>
              </DialogActions>
            </Dialog>

            {/* Exit Front Desk view — password-gated, with the always-available Log out escape. */}
            <ExitFrontDeskDialog open={exitOpen} onClose={() => setExitOpen(false)} />

            {walkInOpen && (
              <WalkInSaleDialog open onClose={() => setWalkInOpen(false)} onSold={() => {}} />
            )}
          </NotificationsProvider>
        </TenantZoneProvider>
      </LocationProvider>
    </ToastProvider>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Typography
      component="div"
      sx={{
        px: 1.5,
        pt: 2,
        pb: 0.5,
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: '0.8px',
        textTransform: 'uppercase',
        color: tokens.textMuted,
      }}
    >
      {children}
    </Typography>
  );
}
